import type { MeteorShower, ShowerPeak } from "../types";

/**
 * Major annual showers. Typical dates seed years after DATED_THROUGH_YEAR.
 * Dated peaks below follow the AMS working list (with IMO timing where it
 * shifts the night).
 */
export const SHOWERS: MeteorShower[] = [
  {
    id: "quadrantids",
    name: "Quadrantids",
    peakMonth: 1,
    peakDay: 3,
    activeStartMonth: 12,
    activeStartDay: 28,
    activeEndMonth: 1,
    activeEndDay: 12,
    zhr: 80,
    notes: "A sharp January peak. Cold, but often a strong show when the moon is down.",
  },
  {
    id: "lyrids",
    name: "Lyrids",
    peakMonth: 4,
    peakDay: 22,
    activeStartMonth: 4,
    activeStartDay: 16,
    activeEndMonth: 4,
    activeEndDay: 25,
    zhr: 18,
    notes: "A reliable spring shower. Modest rates, easy to catch on a camping trip.",
  },
  {
    id: "eta-aquariids",
    name: "Eta Aquariids",
    peakMonth: 5,
    peakDay: 6,
    activeStartMonth: 4,
    activeStartDay: 19,
    activeEndMonth: 5,
    activeEndDay: 28,
    zhr: 50,
    notes: "Best in the pre-dawn south. Halley's Comet debris.",
  },
  {
    id: "delta-aquariids",
    name: "Southern Delta Aquariids",
    peakMonth: 7,
    peakDay: 30,
    activeStartMonth: 7,
    activeStartDay: 12,
    activeEndMonth: 8,
    activeEndDay: 23,
    zhr: 25,
    notes: "A summer southern-sky shower, good from Texas and the Southwest.",
  },
  {
    id: "perseids",
    name: "Perseids",
    peakMonth: 8,
    peakDay: 12,
    activeStartMonth: 7,
    activeStartDay: 17,
    activeEndMonth: 8,
    activeEndDay: 24,
    zhr: 100,
    notes: "The classic August camping shower. Warm nights, high rates.",
  },
  {
    id: "orionids",
    name: "Orionids",
    peakMonth: 10,
    peakDay: 21,
    activeStartMonth: 10,
    activeStartDay: 2,
    activeEndMonth: 11,
    activeEndDay: 7,
    zhr: 20,
    notes: "Fast meteors from Halley's Comet. Nice with Orion rising.",
  },
  {
    id: "leonids",
    name: "Leonids",
    peakMonth: 11,
    peakDay: 17,
    activeStartMonth: 11,
    activeStartDay: 6,
    activeEndMonth: 11,
    activeEndDay: 30,
    zhr: 15,
    notes: "Usually modest, occasionally historic. Cold and worth the alarm clock.",
  },
  {
    id: "geminids",
    name: "Geminids",
    peakMonth: 12,
    peakDay: 14,
    activeStartMonth: 12,
    activeStartDay: 4,
    activeEndMonth: 12,
    activeEndDay: 17,
    zhr: 150,
    notes: "Often the best shower of the year. Bright, plenty, and early-evening friendly.",
  },
  {
    id: "ursids",
    name: "Ursids",
    peakMonth: 12,
    peakDay: 22,
    activeStartMonth: 12,
    activeStartDay: 17,
    activeEndMonth: 12,
    activeEndDay: 26,
    zhr: 10,
    notes: "A quiet year-end shower. Easy to combine with a Geminids trip if you stay out.",
  },
];

/** Last year with hand-entered peak nights. After this, typical dates fill in. */
export const DATED_THROUGH_YEAR = 2027;

/**
 * Night-of dates (the evening of this calendar day). AMS 2026–2027 working
 * list; IMO where it names a different night. Replace this array — or
 * getShowerPeaks() — with an API later.
 */
export const SHOWER_PEAKS: ShowerPeak[] = [
  peak("quadrantids", "2026-01-03", "2025-12-28", "2026-01-12"),
  peak("lyrids", "2026-04-22", "2026-04-16", "2026-04-25"),
  peak("eta-aquariids", "2026-05-06", "2026-04-19", "2026-05-28"),
  peak("delta-aquariids", "2026-07-30", "2026-07-12", "2026-08-23"),
  peak("perseids", "2026-08-12", "2026-07-17", "2026-08-24"),
  peak("orionids", "2026-10-21", "2026-10-02", "2026-11-07"),
  peak("leonids", "2026-11-16", "2026-11-06", "2026-11-30"),
  peak("geminids", "2026-12-13", "2026-12-04", "2026-12-17"),
  peak("ursids", "2026-12-21", "2026-12-17", "2026-12-26"),
  peak("quadrantids", "2027-01-03", "2026-12-28", "2027-01-12"),
  peak("lyrids", "2027-04-22", "2027-04-14", "2027-04-30"),
  peak("eta-aquariids", "2027-05-05", "2027-04-19", "2027-05-28"),
  peak("delta-aquariids", "2027-07-30", "2027-07-12", "2027-08-23"),
  peak("perseids", "2027-08-12", "2027-07-17", "2027-08-24"),
  peak("orionids", "2027-10-21", "2027-10-02", "2027-11-07"),
  peak("leonids", "2027-11-17", "2027-11-06", "2027-11-30"),
  peak("geminids", "2027-12-13", "2027-12-04", "2027-12-17"),
  peak("ursids", "2027-12-21", "2027-12-17", "2027-12-26"),
];

export function showerById(id: string): MeteorShower | undefined {
  return SHOWERS.find((shower) => shower.id === id);
}

/** Local feed. Point this at a calendar API when one exists. */
export function getShowerPeaks(): ShowerPeak[] {
  return SHOWER_PEAKS;
}

function peak(
  showerId: string,
  peakDate: string,
  activeStart: string,
  activeEnd: string,
): ShowerPeak {
  return { showerId, peakDate, activeStart, activeEnd };
}
